/**
 * Formats content text for display in lesson screens
 * - Adds line breaks before * (bullet points) and numbered lists (1), 2), etc.)
 * - Adds newlines before and after colons (:)
 * - Handles HTML escaping
 * - Converts newlines to <br> tags
 * - Handles escaped quotes and numbered lists with periods
 */
export const formatContent = (content) => {
  if (!content) return '';
  
  // First, convert escaped quotes to regular quotes
  let processed = content.replace(/\\"/g, '"');
  processed = processed.replace(/\\'/g, "'");
  
  // Ensure "Eksempler:" appears on a new line (add line break before it if not already there)
  processed = processed.replace(/([^\n])Eksempler:/gi, '$1\nEksempler:');
  
  // Add line breaks before * (bullet points)
  processed = processed.replace(/([^\n])(\*)/g, '$1\n$2');
  
  // Add newline before numbered lists with periods (1., 2., 3., etc.)
  // Match pattern like "1. " or "1." at start of line or after whitespace/punctuation
  processed = processed.replace(/(^|\s|[.:;,!?])(\d+\.\s)/g, '$1\n$2');
  
  // Add newline before numbered lists with parentheses (1), 2), etc.)
  // Only match if preceded by whitespace, punctuation, or start of line (not letters like h1), p2), etc.)
  processed = processed.replace(/(^|\s|[.:;,!?])(\d+\))/g, '$1\n$2');
  
  // Protect code blocks from formatting (content between backticks)
  const codeBlocks = [];
  processed = processed.replace(/```[\s\S]*?```/g, (match) => {
    const placeholder = `__CODE_BLOCK_${codeBlocks.length}__`;
    codeBlocks.push(match);
    return placeholder;
  });
  
  // Also protect inline code (single backticks)
  const inlineCode = [];
  processed = processed.replace(/`[^`\n]+`/g, (match) => {
    const placeholder = `__INLINE_CODE_${inlineCode.length}__`;
    inlineCode.push(match);
    return placeholder;
  });
  
  // Add newline after periods (.) and question marks (?)
  // For colons, be more conservative - only break in natural language contexts
  processed = processed.replace(/([^\n])([.?])([^\n])/g, (match, before, punct, after) => {
    // Don't break if it's part of a numbered list (1. or 2.)
    if (punct === '.' && before.match(/^\d+$/)) {
      return match;
    }
    // Add newline after punctuation
    return `${before}${punct}\n${after}`;
  });
  
  // Handle colons separately - be very conservative
  // Only break after colons for specific natural language labels, never break code syntax
  processed = processed.replace(/([^\n])(:)([^\n])/g, (match, before, punct, after) => {
    // Don't break if it looks like a URL (http://, https://) 
    if (before.match(/https?$/i)) {
      return match;
    }
    // Don't break if it looks like a time (digit before colon)
    if (before.match(/\d$/)) {
      return match;
    }
    
    const beforeTrimmed = before.trim();
    const afterTrimmed = after.trim();
    
    // NEVER break colons that are clearly code syntax:
    // - Object properties: "navn:", "alder:", etc. followed by values
    // - CSS: "color:", "width:", etc.
    // - Any colon followed by quotes, numbers, brackets, or identifiers
    
    // Check if it's code syntax (colon followed by value-like content)
    if (afterTrimmed.match(/^["'\d\[{]/) || // quotes, numbers, brackets, braces
        afterTrimmed.match(/^[a-zA-Z_$]/) || // identifiers
        beforeTrimmed.match(/[{}[\]"'`]/) || // code characters before
        after.match(/[{}[\]"'`]/)) { // code characters after
      return match; // Don't break - it's code
    }
    
    // Only break colons for known natural language labels
    const knownLabels = ['instruksjoner', 'definisjon', 'eksempler', 'forklaring', 'beskrivelse', 'tilgang', 'nested'];
    const beforeLower = beforeTrimmed.toLowerCase();
    const isKnownLabel = knownLabels.some(label => beforeLower.endsWith(label) || beforeLower === label);
    
    if (isKnownLabel && afterTrimmed.match(/^[A-Za-z]/)) {
      // Only break for known labels followed by text
      return `${before}${punct}\n${after}`;
    }
    
    // Default: don't break colons (most are code syntax)
    return match;
  });
  
  // Restore code blocks
  codeBlocks.forEach((block, index) => {
    processed = processed.replace(`__CODE_BLOCK_${index}__`, block);
  });
  inlineCode.forEach((code, index) => {
    processed = processed.replace(`__INLINE_CODE_${index}__`, code);
  });
  
  // Escape HTML to prevent XSS
  const escapeHtml = (text) => {
    const map = {
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#039;'
    };
    return text.replace(/[&<>"']/g, m => map[m]);
  };
  
  // Convert \n (string literal) to actual newlines, then escape HTML
  processed = processed.replace(/\\n/g, '\n');
  processed = escapeHtml(processed);
  
  // Add checkboxes for numbered lists (same as formatInstructions)
  processed = processed.replace(/(^|\n)(\d+[\.\)]\s+)(.*?)(\n|$)/g, '$1<div class="instruction-step"><label class="checkbox-label"><input type="checkbox" class="step-checkbox" /> <span class="step-text">$2$3</span></label></div>$4');
  
  // Make "Definisjon:" and "Instruksjoner:" bold (after HTML escaping so tags aren't escaped)
  processed = processed.replace(/(Definisjon:|Instruksjoner:)/gi, '<strong>$1</strong>');
  
  // Convert newlines to <br> tags for proper rendering
  processed = processed.replace(/\n/g, '<br>');
  
  return processed;
};

/**
 * Formats coding task instructions to put numbered steps on new lines
 * Example: "1) Step one 2) Step two" becomes "1) Step one\n2) Step two"
 * Also handles numbered lists with periods (1., 2., etc.)
 */
export const formatInstructions = (instructions) => {
  if (!instructions) return '';
  
  // First, convert escaped quotes to regular quotes
  let formatted = instructions.replace(/\\"/g, '"');
  formatted = formatted.replace(/\\'/g, "'");
  
  // Add newline before numbered lists with periods (1., 2., 3., etc.)
  // Match pattern like "1. " or "1." at start of line or after whitespace/punctuation
  formatted = formatted.replace(/(^|\s|[.:;,!?])(\d+\.\s)/g, '$1\n$2');
  
  // Add newline before each numbered step with parentheses (1), 2), 3), etc.)
  // Only match if preceded by whitespace, punctuation, or start of line (not letters like h1), p2), etc.)
  formatted = formatted.replace(/(^|\s|[.:;,!?])(\d+\))/g, '$1\n$2');
  
  // Escape HTML to prevent XSS
  const escapeHtml = (text) => {
    const map = {
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#039;'
    };
    return text.replace(/[&<>"']/g, m => map[m]);
  };
  
  formatted = escapeHtml(formatted);
  
  // Add checkboxes for numbered lists
  // Match pattern like "1. Step" or "1) Step" at start of line
  // Wrap the whole line in a label with checkbox
  formatted = formatted.replace(/(^|\n)(\d+[\.\)]\s+)(.*?)(\n|$)/g, '$1<div class="instruction-step"><label class="checkbox-label"><input type="checkbox" class="step-checkbox" /> <span class="step-text">$2$3</span></label></div>$4');
  
  // Convert \n to <br> for HTML rendering
  // formatted = formatted.replace(/\n/g, '<br>');
  
  return formatted;
};

/**
 * Formats expected output text to handle escaped newlines and quotes
 * Converts \n to <br> tags and escaped quotes to regular quotes
 */
export const formatExpectedOutput = (text) => {
  if (!text) return '';
  
  // Convert escaped quotes to regular quotes
  let formatted = text.replace(/\\"/g, '"');
  formatted = formatted.replace(/\\'/g, "'");
  
  // Escape HTML to prevent XSS
  const escapeHtml = (text) => {
    const map = {
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#039;'
    };
    return text.replace(/[&<>"']/g, m => map[m]);
  };
  
  // Convert \n (string literal) to actual newlines, then escape HTML
  formatted = formatted.replace(/\\n/g, '\n');
  formatted = escapeHtml(formatted);
  
  // Convert newlines to <br> tags for proper rendering
  formatted = formatted.replace(/\n/g, '<br>');
  
  return formatted;
};
